import { Spacer } from "@hackthenorth/north";
import { Label } from "north.js";
import React from "react";

import { optionsToNorthV2Options } from "src/shared/utils/react-select";
import { isEmail } from "src/shared/utils/validation";
import {
  PerkStatus,
  SponsorPerksData,
  SponsorPerkType,
} from "src/views/sponsor/perks/types";

import {
  PerkContainer,
  PerkTextInput,
  PerkTextArea,
  PerkError,
  PerkSelect,
} from "../components";
import Header from "../Header";
import { getPerkStatusLabel, perkStatusToBadgeColor } from "../helpers";
import { TextDescription } from "../TextComponents";
import { PerkData } from "../usePerkState";

import { LIGHTNING_CHALLENGE_RELEASE_TIME, TIME_OPTIONS } from "./constants";

export const LIGHTNING_CHALLENGE_VALIDATION_FIELDS: (keyof SponsorPerksData)[] =
  [
    "lightning_challenge_title",
    "lightning_challenge_description",
    "lightning_challenge_representative_name",
    "lightning_challenge_representative_email",
    "lightning_challenge_time_one",
    "lightning_challenge_time_two",
    "lightning_challenge_time_three",
  ];

export const isLightningChallengePerkEnabled =
  Date.now() >= new Date(LIGHTNING_CHALLENGE_RELEASE_TIME).getTime();

export const LightningChallenge: React.FC<PerkData> = ({
  getState,
  updateState,
  isReadOnly,
  error,
}) => {
  const challengeDate1 = getState("lightning_challenge_time_one");
  const challengeDate2 = getState("lightning_challenge_time_two");
  const challengeDate3 = getState("lightning_challenge_time_three");

  const challengeDateError = (challengeDate1 === challengeDate2 ||
    challengeDate1 === challengeDate3 ||
    challengeDate2 === challengeDate3) as boolean;

  return (
    <>
      <Header
        title="Lightning Challenge"
        subtitle="This perk is for a 30-minute Lightning Challenge event during the weekend of Hack the North."
        badge={
          <Label
            color={perkStatusToBadgeColor(
              getState("lightning_challenge_status") as PerkStatus | undefined
            )}
            size="sm"
          >
            {getPerkStatusLabel(
              getState("lightning_challenge_status") as PerkStatus | undefined
            )}
          </Label>
        }
      />
      <PerkContainer
        description={<Description />}
        id={SponsorPerkType.LIGHTNING_CHALLENGE}
        readOnly={isReadOnly}
      >
        {isLightningChallengePerkEnabled && (
          <>
            <PerkTextInput
              title="Title*"
              value={getState("lightning_challenge_title") ?? ""}
              placeholder="Your Lightning Challenge title"
              isReadOnly={isReadOnly}
              onChange={(e) =>
                updateState("lightning_challenge_title", e.target.value)
              }
              error={
                error?.lightning_challenge_title && <>This field is required</>
              }
            />
            <Spacer height={32} />
            <PerkTextArea
              title="Description* (max 250 words)"
              value={getState("lightning_challenge_description") ?? ""}
              placeholder="Description of lightning challenge"
              wordLimit={250}
              isReadOnly={isReadOnly}
              onChange={(e) =>
                updateState("lightning_challenge_description", e.target.value)
              }
              error={
                error?.lightning_challenge_description && (
                  <>This field is required</>
                )
              }
            />
            <Spacer height={32} />
            {challengeDateError && (
              <PerkError
                error={<>Make sure all your time selections are different</>}
              />
            )}
            <Spacer height={8} />
            <PerkSelect
              fullWidth
              size="lg"
              onChange={(e) =>
                updateState("lightning_challenge_time_one", e.target.value)
              }
              value={getState("lightning_challenge_time_one") ?? ""}
              label="Time 1*"
              isReadOnly={isReadOnly}
              error={
                challengeDateError && error?.lightning_challenge_time_one ? (
                  <>You have duplicate time selections</>
                ) : (
                  error?.lightning_challenge_time_one &&
                  !challengeDate1 && <>This field is required</>
                )
              }
            >
              {optionsToNorthV2Options(TIME_OPTIONS)}
            </PerkSelect>
            <Spacer height={32} />
            <PerkSelect
              fullWidth
              size="lg"
              onChange={(e) =>
                updateState("lightning_challenge_time_two", e.target.value)
              }
              value={getState("lightning_challenge_time_two") ?? ""}
              label="Time 2*"
              isReadOnly={isReadOnly}
              error={
                challengeDateError && error?.lightning_challenge_time_two ? (
                  <>You have duplicate time selections</>
                ) : (
                  error?.lightning_challenge_time_two &&
                  !challengeDate2 && <>This field is required</>
                )
              }
            >
              {optionsToNorthV2Options(TIME_OPTIONS)}
            </PerkSelect>
            <Spacer height={32} />
            <PerkSelect
              fullWidth
              size="lg"
              onChange={(e) =>
                updateState("lightning_challenge_time_three", e.target.value)
              }
              value={getState("lightning_challenge_time_three") ?? ""}
              label="Time 3*"
              isReadOnly={isReadOnly}
              error={
                challengeDateError && error?.lightning_challenge_time_three ? (
                  <>You have duplicate time selections</>
                ) : (
                  error?.lightning_challenge_time_three &&
                  !challengeDate3 && <>This field is required</>
                )
              }
            >
              {optionsToNorthV2Options(TIME_OPTIONS)}
            </PerkSelect>
            <Spacer height={32} />
            <PerkTextInput
              title="Who will be running the challenge? (name)*"
              value={getState("lightning_challenge_representative_name") ?? ""}
              placeholder="Representative name"
              isReadOnly={isReadOnly}
              onChange={(e) =>
                updateState(
                  "lightning_challenge_representative_name",
                  e.target.value
                )
              }
              error={
                error?.lightning_challenge_representative_name && (
                  <>This field is required</>
                )
              }
            />
            <Spacer height={32} />
            <PerkTextInput
              title="Who will be running the challenge? (email)*"
              value={getState("lightning_challenge_representative_email") ?? ""}
              placeholder="Representative email"
              isReadOnly={isReadOnly}
              onChange={(e) =>
                updateState(
                  "lightning_challenge_representative_email",
                  e.target.value
                )
              }
              error={
                (error?.lightning_challenge_representative_email && (
                  <>This field is required</>
                )) ||
                (!isEmail(
                  getState("lightning_challenge_representative_email")
                ) && <>Please enter a valid email.</>)
              }
            />
          </>
        )}
      </PerkContainer>
    </>
  );
};
const Description = () =>
  isLightningChallengePerkEnabled ? (
    <>
      <TextDescription>
        This is an opportunity for you to challenge hackers with a fun and
        interesting prompt. Many sponsors in the past have advertised a small
        prize to incentivize hackers to come out as well!
      </TextDescription>
      <Spacer height={16} />
      <TextDescription>
        Please provide a description of your planned lightning challenge for our
        event schedule, as well as a preferred start time. Once this perk has
        been moved to the Complete section, your time will be finalized.
      </TextDescription>
    </>
  ) : (
    <TextDescription>
      Lightning Challenge submissions will open closer to the event!{" "}
      <span role="img" aria-label="eyes">
        👀
      </span>
    </TextDescription>
  );
